/**
 * Typography Configuration
 * Defines the Mensova typography scale and font families.
 */

import { TypographyOptions } from '@mui/material/styles/createTypography';

export const fontFamily = {
  primary: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  display: "'Source Serif 4', Georgia, serif",
  monospace: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
};

export const typography: TypographyOptions = {
  fontFamily: fontFamily.primary,

  h1: {
    fontFamily: fontFamily.display,
    fontSize: '2rem',
    fontWeight: 700,
    lineHeight: 1.15,
    letterSpacing: 0,
  },

  h2: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.334,
    letterSpacing: 0,
  },

  h3: {
    fontSize: '1.25rem',
    fontWeight: 600,
    lineHeight: 1.4,
    letterSpacing: 0,
  },

  h4: {
    fontSize: '1.0625rem',
    fontWeight: 600,
    lineHeight: 1.5,
    letterSpacing: 0,
  },

  h5: {
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.5,
    letterSpacing: 0,
  },

  h6: {
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: 1.6,
    letterSpacing: 0,
  },

  subtitle1: {
    fontSize: '1rem',
    fontWeight: 500,
    lineHeight: 1.75,
    letterSpacing: 0,
  },

  subtitle2: {
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: 1.57,
    letterSpacing: 0,
  },

  body1: {
    fontSize: '0.9375rem',
    fontWeight: 400,
    lineHeight: 1.6,
    letterSpacing: 0,
  },

  body2: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: 0,
  },

  button: {
    fontSize: '0.9375rem',
    fontWeight: 600,
    lineHeight: 1.75,
    letterSpacing: 0,
    textTransform: 'none',
  },

  caption: {
    fontSize: '0.8125rem',
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: 0,
  },

  overline: {
    fontFamily: fontFamily.monospace,
    fontSize: '0.75rem',
    fontWeight: 500,
    lineHeight: 1,
    letterSpacing: 0,
    textTransform: 'uppercase',
  },

  displayXl: {
    fontFamily: fontFamily.display,
    fontSize: '4.75rem',
    fontWeight: 700,
    lineHeight: 1.08,
    letterSpacing: 0,
  },

  displayLg: {
    fontFamily: fontFamily.display,
    fontSize: '3.25rem',
    fontWeight: 700,
    lineHeight: 1.12,
    letterSpacing: 0,
  },

  dataLg: {
    fontFamily: fontFamily.display,
    fontSize: '3.5rem',
    fontWeight: 700,
    lineHeight: 1.08,
    letterSpacing: 0,
  },

  dataMd: {
    fontFamily: fontFamily.monospace,
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1,
    letterSpacing: 0,
  },

  dataSm: {
    fontFamily: fontFamily.monospace,
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1,
    letterSpacing: 0,
  },

  code: {
    fontFamily: fontFamily.monospace,
    fontSize: '0.8125rem',
    fontWeight: 400,
    lineHeight: 1.6,
    letterSpacing: 0,
  },
};

export const fontWeight = {
  light: 300,
  regular: 400,
  medium: 500,
  semiBold: 600,
  bold: 700,
};

export const fontSize = {
  xs: '0.75rem',
  sm: '0.875rem',
  md: '1rem',
  lg: '1.125rem',
  xl: '1.25rem',
  '2xl': '1.5rem',
  '3xl': '1.875rem',
  '4xl': '2.25rem',
  '5xl': '3.25rem',
  '6xl': '4.75rem',
};

export const lineHeight = {
  none: 1,
  tight: 1.25,
  snug: 1.375,
  normal: 1.5,
  relaxed: 1.625,
  loose: 2,
};
